import { useEffect } from "react";
import { Box } from "../Box/Box";
import pxToRem from "../utils/pxToRem";

import type { ProviderType } from "./context";
import { CalendarProvider, useCalendarContext } from "./context";
import { CalendarNavigation } from "./CalendarNavigation/CalendarNavigation";
import { DaysCalendar } from "./DaysCalendar/DaysCalendar";
import { MonthsCalendar } from "./MonthsCalendar/MonthsCalendar";
import { YearsCalendar } from "./YearsCalendar/YearsCalendar";
import { isDateValid } from "./helpers";

export { generateCalendarId } from "./helpers";
export { getCalendarDisclosureLabel } from "./getCalendarDisclosureLabel";

export type { ProviderType };
/**
 * @public
 */
export type CalendarProps = Omit<ProviderType, "children">;

function CalendarContent() {
  const {
    isFirstRender,
    autofocus,
    initialDate,
    selectedDate,
    selectionType,
    calendarType,
    initialCalendarType,
    visible,
    onInnerDateChange,
    setCalendarType,
    onSelectedDateChange,
  } = useCalendarContext();

  useEffect(function validateSelectedDate() {
    if (selectedDate?.length) {
      let isSelectedDateValid: boolean;
      if (selectionType === "day") {
        isSelectedDateValid = isDateValid(selectedDate[0]);
      } else {
        isSelectedDateValid = selectedDate.length === 2 && selectedDate.every(isDateValid);
      }
      if (!isSelectedDateValid) {
        onSelectedDateChange(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- нет уверенности, что предлагаемые зависимости необходимы. Пересмотреть позже
  }, []);

  useEffect(
    function updateInnerDateOnVisibilityChange() {
      const isDateSelected = Boolean(selectedDate?.[0]);
      if (visible && isDateSelected) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- проверка выполнена выше
        onInnerDateChange({ date: selectedDate![0], updateFocus: autofocus });
      } else if (!visible) {
        if (!isDateSelected) {
          onInnerDateChange({ date: initialDate, updateFocus: autofocus });
        }
        setCalendarType(initialCalendarType);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- нет уверенности, что предлагаемые зависимости необходимы. Пересмотреть позже
    [visible]
  );

  useEffect(
    function fillDatesInterval() {
      const isSelectedIntervalHasOneDate =
        selectionType === "interval" && selectedDate && selectedDate.length === 1;
      if (!visible && !isFirstRender && isSelectedIntervalHasOneDate) {
        const intervalDay = selectedDate[0];
        onSelectedDateChange(intervalDay);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- нет уверенности, что предлагаемые зависимости необходимы. Пересмотреть позже
    [visible]
  );

  return (
    <Box width={pxToRem(274)} height={pxToRem(320)} bg="white">
      <CalendarNavigation />
      {calendarType === "days" && <DaysCalendar />}
      {calendarType === "months" && <MonthsCalendar />}
      {calendarType === "years" && <YearsCalendar />}
    </Box>
  );
}

export function Calendar(props: CalendarProps) {
  return (
    <CalendarProvider {...props}>
      <CalendarContent />
    </CalendarProvider>
  );
}
